import { useTranslations } from "@zapier/i18n";
import { Icon } from "@zapier/zinnia-icons";
import {
  Alert,
  AlertDescription,
  Link,
  ServiceIconShell,
  Text,
} from "@zapier/design-system";
import {
  getBuildAssetUrl,
  getAssetIcon,
  assetTypeToSentenceCase,
  AssetTypes,
} from "@zapier/utils";
import styles from "./LinkedAssetsWarning.module.css";

export interface LinkedAsset {
  id: string | number;
  title?: string | null;
  type: AssetTypes;
  // ISO timestamp. Omit when the source data has no last-updated field
  // (e.g. forms); only the connected-assets accordion rows show it.
  updatedAt?: string | null;
}

interface LinkedAssetsListProps {
  linkedAssets: LinkedAsset[];
}

interface LinkedAssetsWarningProps {
  linkedAssets: LinkedAsset[];
}

export const LinkedAssetsList = ({ linkedAssets }: LinkedAssetsListProps) => {
  const t = useTranslations("linked-assets-warning");
  const tFallback = useTranslations("fallbacks");

  return (
    <ul className={styles.linkedAssetList}>
      {linkedAssets.map((asset) => {
        const assetTitle =
          asset.title ||
          (asset.type === AssetTypes.TableSubscription
            ? t("messages.locked-zap")
            : tFallback("untitled-asset", {
                assetType: assetTypeToSentenceCase(asset.type),
              }));
        const icon = (
          <Icon color="BrandOrange" name={getAssetIcon(asset.type)} size={14} />
        );
        const assetUrl = getBuildAssetUrl(asset.type, asset.id);

        return (
          <li
            key={`${asset.type}:${asset.id}`}
            className={styles.linkedAssetItem}
          >
            <ServiceIconShell size="small" hasBorder>
              {icon}
            </ServiceIconShell>
            {assetUrl ? (
              <Link href={assetUrl} target="_blank" rel="noopener noreferrer">
                {assetTitle}
              </Link>
            ) : (
              <Text>{assetTitle}</Text>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const LinkedAssetsWarning = ({ linkedAssets }: LinkedAssetsWarningProps) => {
  const t = useTranslations("linked-assets-warning");

  return (
    <div className={styles.warningContainer}>
      <Alert status="warning">
        <AlertDescription>
          {t("warnings.ownership-change-limitation")}
        </AlertDescription>
      </Alert>

      <Text color="TextWeaker" tag="p">
        {t("descriptions.assets-not-changing-ownership")}
      </Text>
      <LinkedAssetsList linkedAssets={linkedAssets} />
    </div>
  );
};

export default LinkedAssetsWarning;
